/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Modal, ScrollView, useWindowDimensions, View } from 'react-native';
import { getColor, pd } from '@airasia-phoenix/react-native/utils/utils';
import { StyleSheet, UnistylesRuntime } from 'react-native-unistyles';
import { LanguagesView } from '@airasia-rn/ui';
import { CurrencyPage } from '@airasia-rn/ui/pages';
import { CurrencyItem } from '@airasia-rn/ui/widgets';

import {
  CenterSheetView,
  CenterSheetViewRef,
} from '@airasia-phoenix/react-native/components/CentreSheetView';
import TabBarView, {
  TabViewStyle,
  TabsViewProps,
} from '@airasia-phoenix/react-native/components/TabView';
import { MemoizedListViewProps } from '@airasia-phoenix/react-native/components/SearchableListPageView/MemoizedListView';
import { useTranslation } from 'react-i18next';

type LocaleCurrencyTab = 'currency' | 'language';
type LocaleCurrencyTabItem = TabsViewProps & { key: LocaleCurrencyTab };

export interface LocaleCurrencyModalProps {
  visible: boolean;
  onClose: () => void;
  /** Which tab to open by default when the modal becomes visible. */
  defaultTab?: LocaleCurrencyTab;
  currencies: CurrencyItem[];
  selectedCurrency: CurrencyItem;
  onCurrencySelect: (item: CurrencyItem) => void;
  locale: MemoizedListViewProps[];
  selectedLanguage: MemoizedListViewProps;
  onLanguageSelect: (item: MemoizedListViewProps) => void;
  onTabChange?: (tab: LocaleCurrencyTab) => void;
  showCurrency?: boolean;
  showLocale?: boolean;
}

const MODAL_MAX_WIDTH = 950;
const MODAL_COMPACT_MAX_WIDTH = `calc(100% - ${
  pd.ref.space.spacing40.px * 2
}px)`;

const LocaleCurrencyModal: React.FC<LocaleCurrencyModalProps> = ({
  visible,
  onClose,
  defaultTab = 'currency',
  currencies,
  selectedCurrency,
  onCurrencySelect,
  locale,
  selectedLanguage,
  onLanguageSelect,
  onTabChange,
  showCurrency = true,
  showLocale = true,
}) => {
  const sheetRef = useRef<CenterSheetViewRef>(null);
  const { width } = useWindowDimensions();
  const { breakpoints } = UnistylesRuntime;
  const { t } = useTranslation('account');

  const availableTabs: LocaleCurrencyTabItem[] = useMemo(() => {
    const tabs: LocaleCurrencyTabItem[] = [];

    if (showCurrency) {
      tabs.push({ key: 'currency', text: t('currencyView.currencies') });
    }

    if (showLocale) {
      tabs.push({ key: 'language', text: t('currencyView.languages') });
    }

    return tabs;
  }, [showCurrency, showLocale, t]);

  const defaultTabAvailable = availableTabs.some(
    (tab) => tab.key === defaultTab,
  );
  const resolvedDefaultTab = defaultTabAvailable
    ? defaultTab
    : availableTabs[0]?.key;
  const [activeTab, setActiveTab] = useState<LocaleCurrencyTab>(
    resolvedDefaultTab ?? defaultTab,
  );

  useEffect(() => {
    // Modal only renders children when visible=true,
    // so this triggers the sheet entry animation when it becomes visible.
    if (visible) {
      sheetRef.current?.open();
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    if (resolvedDefaultTab) {
      setActiveTab(resolvedDefaultTab);
    }
  }, [resolvedDefaultTab, visible]);

  if (!availableTabs.length) return null;

  const currentTabIndex = Math.max(
    availableTabs.findIndex((tab) => tab.key === activeTab),
    0,
  );

  const modalMaxWidth =
    width < breakpoints.lg ? MODAL_COMPACT_MAX_WIDTH : MODAL_MAX_WIDTH;

  return (
    <Modal
      transparent
      visible={visible}
      animationType="fade"
      onRequestClose={onClose}
    >
      <View
        style={[
          styles.backdrop,
          { backgroundColor: getColor(pd.sys.colour.overlay) },
        ]}
      >
        <CenterSheetView
          ref={sheetRef}
          showHeader
          onCenterSheetViewClose={onClose}
          maxWidth={modalMaxWidth}
        >
          <ScrollView contentContainerStyle={styles.scrollContent}>
            <TabBarView
              data={availableTabs}
              peek={0}
              tabStyle={TabViewStyle.STANDARD}
              currentTabIndex={currentTabIndex}
              onSelect={(item: TabsViewProps) => {
                setActiveTab(item.key as LocaleCurrencyTab);
                onTabChange?.(item.key as LocaleCurrencyTab);
              }}
            />

            <View style={styles.container}>
              {activeTab === 'currency' ? (
                <CurrencyPage
                  showHeader={false}
                  onClose={onClose}
                  currencyList={currencies}
                  selectedCurrencyValue={selectedCurrency}
                  onSelect={onCurrencySelect}
                />
              ) : (
                <LanguagesView
                  showHeader={false}
                  onClose={onClose}
                  localeList={locale}
                  selectedLanguageValue={selectedLanguage}
                  onSelect={onLanguageSelect}
                />
              )}
            </View>
          </ScrollView>
        </CenterSheetView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create((pd) => ({
  backdrop: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    marginTop: pd.ref.space.spacing20.px,
  },
  scrollContent: {
    paddingHorizontal: pd.ref.space.spacing40.px,
  },
}));

export default LocaleCurrencyModal;
